import * as React from 'react';
import { RouteComponentProps, browserHistory } from 'react-router';
import { SortableContainer, SortableElement, arrayMove } from 'react-sortable-hoc';
import { reduxForm, InjectedFormProps, Field, getFormSyncErrors } from 'redux-form';
import { translate } from 'react-i18next';
const { connect } = require('react-redux');
const { asyncConnect } = require('redux-connect');
import { IStore } from 'redux/IStore';
import {
	getMenuItemData,
	updateSectionsOrder,
	saveSection,
	editSection,
	saveMenuItem,
	removeSection,
	addMenuItem,
} from 'modules/menuitem/menuitem';
import { showModal, closeModal } from 'modules/modal';
import * as c from 'models/constants';
import { IFetchDefault } from 'redux/modules/fetch';
import { ILanguage } from 'models/common';
import { IModalAction } from 'models/modal';
import { IMenuItem, IMenuItemData, IMenuItemDataAction } from 'models/menuitem/menuitem';
import { IMagazinePageSection } from 'models/common/section';
import { ErrorSummary, Button, MenuItemButtons } from 'components';
import { PortalDatePicker, PortalSelectMenu, PortalInputField, PortalTextareaField } from 'components/FormControls';
import { required, maxLength, alphabetAndMinusCharacter, dateRequired } from 'helpers/validationattributes';
import {
	AddSectionButton,
	TwoColumnPage,
	SidebarBlock,
	PortalPageSection,
	ButtonsSection,
} from 'components/PortalStructure';
import { InjectedRouter } from 'react-router';
import { getMenuItemTypeByPathname } from 'helpers/common';
import { MagazinePageSectionRenderer } from 'components/Magazine/Page/Sections';
import { MenuItemType } from 'models/enums/menuitemtype';
import { PortalPageSectionRenderer } from 'components/PortalPage/Sections';
import { IPortalPageSection } from 'models/portal/page/portalpagesection';
import { preventRouterLeave } from 'helpers/hooks';

const maxLength255 = maxLength(255);
const maxLength4000 = maxLength(4000);

interface IProps extends RouteComponentProps<IParams, {}> {
	menuItemData: IMenuItemData;
	initialValues?: IMenuItem;
	currentFormValues?: IMenuItem;
	fetchDefault: IFetchDefault;
	languageDomainValues: ILanguage[];
	location: any;
	router: InjectedRouter;
	route: any;

	/* form props */
	pristine: boolean;
	dirty: boolean;
	submitting: boolean;
	handleSubmit: any;
	submitFailed: boolean;
	formErrors?: any;

	/* functions props */
	showModal: (modalType: string, modalProps: any) => IModalAction;
	closeModal: () => IModalAction;
	saveSection: (form: IMagazinePageSection) => void;
	editSection: (form: IMagazinePageSection, index: number) => void;
	removeSection: (index: number) => void;
	saveMenuItem: (menuItem: IMenuItem, menuItemType: MenuItemType) => void;
	addMenuItem: (menuItem: IMenuItem, menuItemType: MenuItemType) => void;
	updateSectionsOrder: (sections: any[]) => IMenuItemDataAction;
	t?: (key: string) => string;
}

interface IParams extends InjectedFormProps {
	id: string;
}

@asyncConnect([
	{
		promise: (options) => {
			const {
				store: { dispatch },
				params: { id },
			} = options;

			return dispatch(getMenuItemData(id));
		},
	},
])
@preventRouterLeave()
@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class ManageMenuItemForm extends React.Component<IProps, {}> {
	private menuItemType: MenuItemType;

	constructor(props) {
		super(props);

		this.onSortEnd = this.onSortEnd.bind(this);
		this.openSelectSection = this.openSelectSection.bind(this);
		this.openAddSection = this.openAddSection.bind(this);
		this.openEditSection = this.openEditSection.bind(this);
		this.openDeleteSection = this.openDeleteSection.bind(this);
		this.saveSection = this.saveSection.bind(this);
		this.editSection = this.editSection.bind(this);
		this.onSubmit = this.onSubmit.bind(this);
		this.deleteSection = this.deleteSection.bind(this);
		this.getSectionRenderer = this.getSectionRenderer.bind(this);
		this.openDeleteSection = this.openDeleteSection.bind(this);
	}

	public componentDidMount() {
		this.menuItemType = getMenuItemTypeByPathname(this.props.location.pathname);
	}

	public onSortEnd = ({ oldIndex, newIndex }) => {
		if (!this.props.menuItemData.page) {
			return;
		}
		const sections: any = arrayMove(this.props.menuItemData.page.sections as any, oldIndex, newIndex);
		this.props.updateSectionsOrder(sections);
		this.forceUpdate();
	};

	private openSelectSection() {
		let sectionSelector = '';

		switch (this.menuItemType) {
			case MenuItemType.Portal:
				sectionSelector = 'PortalPageSectionSelector';
				break;

			case MenuItemType.Magazine:
				sectionSelector = 'MagazinePageSectionSelector';
				break;

			default:
				break;
		}

		this.props.showModal(sectionSelector, {
			addSection: this.openAddSection,
			closeModal: this.props.closeModal,
			showHeaderCloseButton: true,
		});
	}

	private openAddSection(sectionType) {
		this.props.closeModal();
		this.props.showModal(sectionType, {
			saveSection: this.saveSection,
			closeModal: this.props.closeModal,
			showModal: this.props.showModal,
			baseUrl: this.props.fetchDefault.baseUrlClient,
			showHeaderCloseButton: true,
		});
	}

	private openEditSection(section: IMagazinePageSection, index: number) {
		this.props.showModal(section.type, {
			section,
			index,
			saveSection: this.editSection,
			closeModal: this.props.closeModal,
			showModal: this.props.showModal,
			baseUrl: this.props.fetchDefault.baseUrlClient,
			showHeaderCloseButton: true,
		});
	}

	private editSection(section: IMagazinePageSection, index: number) {
		this.props.editSection(section, index);
		this.props.closeModal();
		this.forceUpdate();
	}

	private saveSection(form: IMagazinePageSection) {
		this.props.saveSection(form);
		this.props.closeModal();
		this.forceUpdate();
	}

	private openDeleteSection(index: number) {
		const dialogContent = (
			<div>
				<img src={require('containers/Html/icons/icon_notification.png')} />
				<h2>{this.props.t('BSPortal_Common_ConfirmationDialog_Sure')}</h2>
				<p>{this.props.t('Administration_MenuItem_ManageMenuItem_Section_AboutDelete')}</p>
				<p>{this.props.t('BSPortal_Common_ConfirmationDialog_Confirm')}</p>
			</div>
		);

		this.props.showModal('ConfirmationDialog', {
			confirmAction: this.deleteSection,
			value: index,
			yesButtonText: this.props.t('BSPortal_Common_YesDelete'),
			noButtonText: this.props.t('BSPortal_Common_NoWait'),
			content: dialogContent,
			closeModal: this.props.closeModal,
		});
	}

	private deleteSection(confirm: boolean, index: number) {
		if (confirm) {
			this.props.removeSection(index);
		}
		this.props.closeModal();
	}

	private onSubmit(form: IMenuItem) {
		if (!this.props.menuItemData.isEdit) {
			this.props.addMenuItem(form, this.menuItemType);
		} else {
			this.props.saveMenuItem(form, this.menuItemType);
		}
	}

	private cancelClick() {
		browserHistory.goBack();
	}

	private mapErrorKeyToMessage(key: string, t: (key: string) => string) {
		switch (key) {
			case 'title':
				return t('Administration_MenuItem_ManageMenuItem_Label_Title');
			case 'code':
				return t('Administration_MenuItem_ManageMenuItem_Label_FriendlyUrl');
			case 'languageKey':
				return t('Administration_MenuItem_ManageMenuItem_Label_Language');
			case 'releaseDate':
				return t('Administration_MenuItem_ManageMenuItem_Label_ReleaseDate');
			case 'expireDate':
				return t('Administration_MenuItem_ManageMenuItem_Label_ExpireDate');
			default:
				return key;
		}
	}

	private getSectionRenderer(item: any) {
		let sectionRenderer: JSX.Element = null;

		switch (this.menuItemType) {
			case MenuItemType.Portal:
				sectionRenderer = <PortalPageSectionRenderer portalPageSection={item} isAdmin={true} />;
				break;

			case MenuItemType.Magazine:
				sectionRenderer = <MagazinePageSectionRenderer section={item} />;
				break;

			default:
				break;
		}

		return sectionRenderer;
	}

	/** --------------RENDER ----------------------- */
	public render() {
		let sections: IPortalPageSection[] | IMagazinePageSection[];
		if (this.props.menuItemData.page) {
			sections = this.props.menuItemData.page.sections;
			const type = 'ManageType13'; // Hero banner section

			// Sorts all sections and puts hero banner sections at the beginning of the array
			sections.sort((a, b) => {
				if (a.type === type && b.type === type) {
					return 0;
				}
				if (a.type !== type && b.type === type) {
					return 1;
				}
				if (a.type === type && b.type !== type) {
					return -1;
				}
			});
		}
		const { t, handleSubmit, formErrors, submitFailed, location } = this.props;
		const { message, error } = this.props.menuItemData;

		const errorSummaryProps = {
			formErrors,
			submitFailed,
			error,
			message,
			location,
			mapErrorKeyToMessage: this.mapErrorKeyToMessage,
		};

		const SortableItem = SortableElement(({ item, i }) => (
			<div className="movableSection">
				<li>
					{this.getSectionRenderer(item)}
					<MenuItemButtons
						item={item}
						i={i}
						openEditSection={this.openEditSection}
						openDeleteSection={this.openDeleteSection}
					/>
				</li>
			</div>
		));

		const SortableList = SortableContainer(({ items }) => {
			return (
				<ul className="sortedSectionsList">
					{items && items.map((item, index) => <SortableItem key={index} index={index} item={item} i={index} />)}
				</ul>
			);
		});

		let languageOptions = [];
		if (this.props.languageDomainValues.length > 0) {
			languageOptions = this.props.languageDomainValues.map((language: ILanguage) => {
				return {
					key: language.key,
					value: language.value,
				};
			});
		}

		const leftContent = (
			<div>
				<AddSectionButton onClick={this.openSelectSection}>
					{t('Administration_MenuItem_ManageMenuItem_AddSection')}
				</AddSectionButton>
				<SortableList items={sections} onSortEnd={this.onSortEnd} />
				<ButtonsSection usage="admin" customClassName="buttonsSectionAdmin__mobile">
					<div className="row">
						<div className="col-sm-6">
							<Button type="submit" usage="admin">
								{t('BSPortal_Common_Save')}
							</Button>
						</div>
						<div className="col-sm-6">
							<Button usage="admin" onClick={this.cancelClick}>
								{t('BSPortal_Common_Cancel')}
							</Button>
						</div>
					</div>
				</ButtonsSection>
			</div>
		);

		const rightContent = (
			<SidebarBlock>
				<PortalPageSection usage="admin">
					<div>
						<ErrorSummary {...errorSummaryProps} />
					</div>

					<div className="row">
						<div className="col-md-12">
							<Field
								name="title"
								component={PortalInputField}
								usage="admin"
								type="text"
								autoFocus={true}
								validate={[required, maxLength255]}
								label={t('Administration_MenuItem_ManageMenuItem_Label_Title')}
							/>
						</div>
					</div>

					<div className="row">
						<div className="col-md-12">
							<Field
								name="code"
								component={PortalInputField}
								usage="admin"
								type="text"
								validate={[required, alphabetAndMinusCharacter]}
								label={t('Administration_MenuItem_ManageMenuItem_Label_FriendlyUrl')}
							/>
						</div>
					</div>

					<div className="row">
						<div className="col-md-12">
							<Field
								name="description"
								component={PortalTextareaField}
								usage="admin"
								type="textarea"
								rows="6"
								validate={[maxLength4000]}
								label={t('Administration_MenuItem_ManageMenuItem_Label_Description')}
							/>
						</div>
					</div>

					<div className="row">
						<div className="col-md-12">
							<Field
								name="releaseDate"
								component={PortalDatePicker}
								usage="admin"
								placeholderText={t('Administration_MenuItem_ManageMenuItem_Label_ReleaseDate')}
								showMonthDropdown={true}
								showYearDropdown={true}
								type="text"
								label={t('Administration_MenuItem_ManageMenuItem_Label_ReleaseDate')}
								validate={[required, dateRequired]}
							/>
						</div>
					</div>

					<div className="row">
						<div className="col-md-12">
							<Field
								name="expireDate"
								component={PortalDatePicker}
								usage="admin"
								placeholderText={t('Administration_MenuItem_ManageMenuItem_Label_ExpireDate')}
								showMonthDropdown={true}
								showYearDropdown={true}
								type="text"
								label={t('Administration_MenuItem_ManageMenuItem_Label_ExpireDate')}
								validate={[required, dateRequired]}
							/>
						</div>
					</div>

					<div className="row">
						<div className="col-md-12">
							<Field
								name="languageKey"
								component={PortalSelectMenu}
								usage="admin"
								items={languageOptions}
								isRequired={true}
								validate={[required]}
								label={t('Administration_MenuItem_ManageMenuItem_Label_Language')}
								searchable={false}
							/>
						</div>
					</div>
				</PortalPageSection>
				<ButtonsSection usage="admin" customClassName="buttonsSectionAdmin__desktop">
					<div className="row">
						<div className="col-sm-6">
							<Button type="submit" usage="admin">
								{t('BSPortal_Common_Save')}
							</Button>
						</div>
						<div className="col-sm-6">
							<Button usage="admin" onClick={this.cancelClick}>
								{t('BSPortal_Common_Cancel')}
							</Button>
						</div>
					</div>
				</ButtonsSection>
			</SidebarBlock>
		);

		return (
			<div className="manageMenuItemWrapper">
				<form onSubmit={handleSubmit(this.onSubmit)}>
					<TwoColumnPage leftColumnContent={leftContent} rightColumnContent={rightContent} />
				</form>
			</div>
		);
	}
}

function mapStateToProps(state: IStore) {
	let currentFormValues: IMenuItem = null;
	if (state.form[c.FORM_NAME_MANAGE_MENU_ITEM] && state.form[c.FORM_NAME_MANAGE_MENU_ITEM].values) {
		currentFormValues = state.form[c.FORM_NAME_MANAGE_MENU_ITEM].values;
	} else {
		currentFormValues = state.menuItem.menuItem;
	}

	return {
		fetchDefault: state.fetchDefault,
		menuItemData: state.menuItem,
		initialValues: state.menuItem.menuItem,
		languageDomainValues: state.domainValues.languages,
		currentFormValues,
		formErrors: getFormSyncErrors(c.FORM_NAME_MANAGE_MENU_ITEM)(state),
	};
}

let ManageMenuItem = reduxForm(
	{
		form: c.FORM_NAME_MANAGE_MENU_ITEM,
		enableReinitialize: true,
		touchOnChange: false,
		touchOnBlur: false,
	},
	mapStateToProps,
)(ManageMenuItemForm);

ManageMenuItem = connect(mapStateToProps, (dispatch) => ({
	updateSectionsOrder: (sections: IMagazinePageSection[]) => dispatch(updateSectionsOrder(sections)),
	saveSection: (form: IMagazinePageSection) => dispatch(saveSection(form)),
	editSection: (form: IMagazinePageSection, index: number) => dispatch(editSection(form, index)),
	removeSection: (index: number) => dispatch(removeSection(index)),
	saveMenuItem: (menuItem: IMenuItem, menuItemType: MenuItemType) => dispatch(saveMenuItem(menuItem, menuItemType)),
	addMenuItem: (menuItem: IMenuItem, menuItemType: MenuItemType) => dispatch(addMenuItem(menuItem, menuItemType)),
	closeModal: () => dispatch(closeModal()),
	showModal: (modalType: string, modalProps: any) => dispatch(showModal(modalType, modalProps)),
}))(ManageMenuItem);

export { ManageMenuItem };
